/**
 * Detects when a user lands from a tagged (paid / affiliate) campaign link and
 * logs a dedicated `affiliate_landing` event to the custom Firestore logs
 * collection.
 *
 * Why a dedicated event when `first_visit` already carries the UTM params:
 *  - `first_visit` fires exactly once per browser, ever. A returning visitor who
 *    first arrived organically and later clicks an affiliate ad would never be
 *    re-attributed to the campaign. `affiliate_landing` fires per arrival, so the
 *    click is always attributed.
 *  - It gives the funnel analyzer an unambiguous *paid-only* entry point
 *    (`first_event = affiliate_landing`), excluding organic traffic that also
 *    produces `first_visit`.
 *
 * The event carries the raw campaign dimensions so the standalone funnel can
 * break down by service (utm_source) × language (utm_campaign) × concept
 * (utm_content) × ad size (utm_creative_format).
 */

import { logEvent } from "./loggingService";
import { isAnalyticsAllowed, getStoredConsent } from "./cookieConsentService";
import { getStoredRegionInfo } from "./regionService";

const AFFILIATE_LANDING_LOGGED_PREFIX = "affiliate_landing_logged_v1:";

/** Campaign params we read off the landing URL, mapped to their meaning. */
const UTM_KEYS = [
  "utm_source", // affiliate service / network
  "utm_medium", // channel (e.g. "affiliate")
  "utm_campaign", // language of the creative (e.g. "de" / "en")
  "utm_content", // creative concept (e.g. "concept-03")
  "utm_creative_format", // ad size (e.g. "300x250")
  "utm_term", // optional: network placement / zone macro
  "utm_id",
  "utm_source_platform",
  "utm_marketing_tactic",
] as const;

export type AffiliateArrivalPayload = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_creative_format?: string;
  utm_term?: string;
  utm_id?: string;
  utm_source_platform?: string;
  utm_marketing_tactic?: string;
  landing_path: string;
  landing_page_full_url: string;
  referrer_page_url?: string;
  detectedAt: string;
};

function isAllowed(): boolean {
  const consent = getStoredConsent();
  const region = getStoredRegionInfo();
  return isAnalyticsAllowed(region?.regionBucket, consent);
}

/**
 * Parses campaign params off the current URL. Returns null when no `utm_source`
 * is present (untagged / organic traffic), so this only fires for campaign
 * clicks.
 */
export function parseAffiliateArrivalFromUrl(): AffiliateArrivalPayload | null {
  if (typeof window === "undefined") return null;

  const { pathname, href, search } = window.location;
  const params = new URLSearchParams(search);

  const source = params.get("utm_source");
  if (!source) return null;

  const payload: AffiliateArrivalPayload = {
    landing_path: pathname,
    landing_page_full_url: href,
    detectedAt: new Date().toISOString(),
  };

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) {
      (payload as Record<string, string>)[key] = value;
    }
  }

  const referrer = typeof document !== "undefined" ? document.referrer?.trim() : "";
  if (referrer) {
    payload.referrer_page_url = referrer;
  }

  return payload;
}

/**
 * Stable key identifying this specific creative arrival, used to de-duplicate
 * within a browser session while still re-firing for a genuinely different
 * campaign/concept/size combination.
 */
function arrivalDedupeKey(payload: AffiliateArrivalPayload): string {
  return [
    payload.utm_source ?? "",
    payload.utm_campaign ?? "",
    payload.utm_content ?? "",
    payload.utm_creative_format ?? "",
    payload.utm_term ?? "",
  ].join("|");
}

function alreadyLoggedThisSession(dedupeKey: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.sessionStorage.getItem(
        AFFILIATE_LANDING_LOGGED_PREFIX + dedupeKey
      ) === "1"
    );
  } catch {
    return false;
  }
}

function markLoggedThisSession(dedupeKey: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      AFFILIATE_LANDING_LOGGED_PREFIX + dedupeKey,
      "1"
    );
  } catch {
    // sessionStorage unavailable (private mode / quota) — logging a possible
    // duplicate is acceptable and preferable to dropping the event.
  }
}

/**
 * If the current URL is a tagged campaign link, logs an `affiliate_landing`
 * event once per unique creative per browser session. Respects analytics
 * consent. Safe to call on every route change.
 */
export async function logAffiliateArrivalIfPresent(): Promise<void> {
  if (!isAllowed()) return;

  const payload = parseAffiliateArrivalFromUrl();
  if (!payload) return;

  const dedupeKey = arrivalDedupeKey(payload);
  if (alreadyLoggedThisSession(dedupeKey)) return;
  markLoggedThisSession(dedupeKey);

  await logEvent("affiliate_landing", payload as Record<string, unknown>);
}
